/**
 * "Remind me" for any event (2026-09-18 owner ask): the reminder the transfer
 * bonus card already sets, offered on every event card (Calendar, the
 * Profile radar rows). ONE flow, shared:
 *
 *   1. the Settings row for event reminders decides what the tap may do
 *      (Off keeps only Your plan, In-app the plan and the in-app flags, Push
 *      schedules a notification);
 *   2. the OS permission is re-checked against the device on the tap, and
 *      whatever stops the notification is said, never a "Reminder set." over
 *      nothing (2026-09-10 owner report);
 *   3. a LOCAL notification is scheduled at the event's LOCAL start, the same
 *      wall-clock recovery the calendar invites use.
 *
 * A local notification is delivered by iOS / Android at the scheduled time
 * with the app closed or force-quit: the OS holds it, not the app. It is not
 * push (nothing is sent from a server), which is why the Settings row calls
 * the channel "Push" only for the trainer's benefit.
 */
import { eventCountdownDate } from "./calendarLinks";
import { toggleCalendarTodo, listCalendarTodos } from "./calendarTodoStore";
import { deviceDelivery } from "./localNotifPrefs";
import {
  describeScheduleResult,
  requestNotificationPermission,
  scheduleLocalNotificationAt,
} from "./notifications";
import { liveActivityReminderAvailable, scheduleLiveActivityReminder } from "./liveActivities";
import { getLiveEventPrefs, isTimeBoxedEvent, leadLabel } from "./liveEventPrefs";

export interface RemindableEvent {
  id: string;
  title: string;
  /** Local wall-clock start, "YYYY-MM-DD" or "YYYY-MM-DDTHH:mm[:ss]". */
  start?: string | null;
  end?: string | null;
  /** The calendar's display date label, when the caller has one. */
  date?: string;
  /** For the Live Activity card (lib/liveActivities): the kind, the Pokémon, the bonus. */
  category?: string;
  spawns?: ReadonlyArray<{ name?: string; speciesId?: number; shinyBoost?: boolean }>;
  bonuses?: ReadonlyArray<string>;
  /** One-place event: the standing choice covers it only for an attendee. */
  geoLocked?: boolean;
}

export type EventReminderOutcome =
  | { kind: "past" }
  | { kind: "plan-only"; channel: "off" | "in-app" }
  | { kind: "denied" }
  | { kind: "set"; when: string }
  /** iPhone: a Lock Screen countdown an hour before ("push"), or on screen now ("local"). */
  | { kind: "set-live"; when: string; mode: "push" | "local" }
  | { kind: "failed"; detail: string };

/** FNV-1a of the event id, folded to a positive int: the notification's id. */
export function notificationIdForEvent(eventId: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < eventId.length; i++) {
    h ^= eventId.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return Math.abs(h >>> 1) || 1;
}

/** The event's local start as a Date, or null when unknown or already past. */
export function eventReminderAt(start: string | null | undefined, now: number = Date.now()): Date | null {
  if (!start) return null;
  const d = eventCountdownDate(start);
  if (Number.isNaN(d.getTime()) || d.getTime() <= now) return null;
  return d;
}

/** "Thu, Oct 15, 10:00 AM", in the device's locale and zone. */
export function eventReminderLabel(at: Date): string {
  return at.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

// ── Which events have a reminder set (this device) ───────────────────────────
const STORAGE_KEY = "nexus.eventReminders.v1";
export const EVENT_REMINDERS_EVENT = "nexus:event-reminders-changed";
let cache: Set<string> | null = null;

function readSet(): Set<string> {
  if (cache) return cache;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : [];
    cache = new Set(Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === "string") : []);
  } catch {
    cache = new Set();
  }
  return cache;
}

function remember(eventId: string): void {
  const set = readSet();
  set.add(eventId);
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...set]));
  } catch {
    /* private mode: the session still knows */
  }
  try {
    window.dispatchEvent(new Event(EVENT_REMINDERS_EVENT));
  } catch {
    /* no window */
  }
}

export function hasEventReminder(eventId: string): boolean {
  return readSet().has(eventId);
}

/** Tests only. */
export function __resetEventRemindersForTest(): void {
  cache = null;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Set the reminder: the event joins Your plan whatever the channel, and a
 * notification is scheduled when Settings and the OS allow it.
 */
export async function setEventReminder(ev: RemindableEvent, now: number = Date.now()): Promise<EventReminderOutcome> {
  const at = eventReminderAt(ev.start, now);
  if (!at) return { kind: "past" };
  const planned = listCalendarTodos(now).some((t) => t.eventId === ev.id);
  if (!planned) {
    toggleCalendarTodo({
      eventId: ev.id,
      date: ev.date ?? eventReminderLabel(at),
      title: ev.title,
      start: ev.start ?? undefined,
      end: ev.end ?? undefined,
    });
  }
  const delivery = deviceDelivery("event-reminders");
  if (!delivery.banner) return { kind: "plan-only", channel: delivery.toast ? "in-app" : "off" };
  // The standing choice (lib/liveEventPrefs, 2026-10-30) sets the lead and
  // whether a tapped reminder is the card or a notification; a trainer who
  // never made one keeps the hour-before card.
  const prefs = getLiveEventPrefs();
  const leadMinutes = prefs ? prefs.leadMinutes : 60;
  const leadMs = leadMinutes * 60_000;
  // A card only for an event with a moment to count down to (owner
  // direction 2026-10-31, lib/liveEventPrefs): a week-long rotation or a
  // months-long Timed Research gets the notification below instead.
  const wantsCard = (!prefs || prefs.delivery === "live") && isTimeBoxedEvent(ev);
  // iPhone (2026-10-23): the reminder is a Lock Screen countdown from the
  // lead before the start, not a notification at it. Needs no notification
  // permission; a phone that cannot do it keeps the notification below.
  if (wantsCard && (await liveActivityReminderAvailable(at.getTime(), now, leadMs))) {
    const live = await scheduleLiveActivityReminder(ev, now, { leadMs });
    if (live.ok) {
      remember(ev.id);
      return { kind: "set-live", when: eventReminderLabel(at), mode: live.mode };
    }
  }
  let perm: "granted" | "denied" | "prompt" | "unknown" = "unknown";
  try {
    perm = await requestNotificationPermission({ reprompt: true });
  } catch {
    perm = "unknown";
  }
  if (perm !== "granted") return { kind: "denied" };
  // The notification lands the lead before the start when a standing choice
  // names one (at the start with no choice, as it always did, or when the
  // lead has already passed).
  const notifyLeadMinutes = prefs ? prefs.leadMinutes : 0;
  const lead = leadLabel(notifyLeadMinutes);
  const notifyAt = new Date(at.getTime() - notifyLeadMinutes * 60_000);
  const early = lead !== "" && notifyAt.getTime() > now;
  const result = await scheduleLocalNotificationAt({
    id: notificationIdForEvent(ev.id),
    title: early ? `${ev.title} starts in ${lead}` : `${ev.title} is live`,
    body: early ? `${ev.title} starts in ${lead} in Pokémon GO.` : `${ev.title} just started in Pokémon GO.`,
    at: early ? notifyAt : at,
  });
  if (!result.ok) return { kind: "failed", detail: describeScheduleResult(result) };
  remember(ev.id);
  return { kind: "set", when: eventReminderLabel(at) };
}
